/**
 * Treppan Academy - Learning Path Detail
 * View a learning path with its courses and progress
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Route, BookOpen, Clock, Award, ChevronRight, Play,
  CheckCircle2, Lock, ArrowLeft, Target, Zap, Trophy
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  goldHover: '#B5952F',
  success: '#10B981',
  purple: '#A855F7',
  blue: '#3B82F6',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const difficultyColors = {
  beginner: colors.success,
  intermediate: colors.gold,
  advanced: colors.purple
};

const CourseStep = ({ course, index, isCompleted, isCurrentCourse, isLocked, onStart }) => {
  const navigate = useNavigate();
  
  return (
    <div 
      className={`relative flex gap-4 ${index > 0 ? 'pt-6' : ''}`}
      data-testid={`course-step-${index}`}
    >
      {/* Timeline connector */}
      {index > 0 && (
        <div 
          className="absolute left-6 top-0 w-0.5 h-6"
          style={{ backgroundColor: isCompleted ? colors.success : colors.border }}
        />
      )}
      
      {/* Step indicator */}
      <div 
        className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
          isCurrentCourse ? 'ring-4' : ''
        }`}
        style={{ 
          backgroundColor: isCompleted ? colors.success : isLocked ? colors.surface : `${colors.gold}20`,
          border: `2px solid ${isCompleted ? colors.success : isLocked ? colors.textMuted : colors.gold}`,
          ringColor: `${colors.gold}40`
        }}
      >
        {isCompleted ? (
          <CheckCircle2 className="h-6 w-6 text-white" />
        ) : isLocked ? (
          <Lock className="h-5 w-5" style={{ color: colors.textMuted }} />
        ) : (
          <span className="text-lg font-bold" style={{ color: colors.gold }}>{index + 1}</span>
        )}
      </div>
      
      {/* Course card */}
      <Card 
        className={`flex-1 transition-all ${isLocked ? 'opacity-60' : 'cursor-pointer hover:scale-[1.01]'}`}
        style={{ 
          backgroundColor: colors.surface, 
          border: `1px solid ${isCurrentCourse ? colors.gold : colors.border}`,
          boxShadow: isCurrentCourse ? `0 0 20px ${colors.gold}20` : 'none'
        }}
        onClick={() => !isLocked && navigate(`/academy/courses/${course.id}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {/* Thumbnail */}
            <img 
              src={course.thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=200'}
              alt={course.title}
              className="w-20 h-14 rounded-lg object-cover"
            />
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 
                  className="font-semibold truncate"
                  style={{ color: colors.textHeading }}
                >
                  {course.title}
                </h3>
                {isCompleted && (
                  <Badge style={{ backgroundColor: `${colors.success}20`, color: colors.success }}>
                    Completed
                  </Badge>
                )}
                {isCurrentCourse && !isCompleted && (
                  <Badge style={{ backgroundColor: `${colors.gold}20`, color: colors.gold }}>
                    Current
                  </Badge>
                )}
              </div>
              
              <p 
                className="text-sm mb-2 line-clamp-1"
                style={{ color: colors.textBody }}
              >
                {course.description}
              </p>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" style={{ color: colors.textMuted }} />
                  <span className="text-xs" style={{ color: colors.textMuted }}>
                    {course.duration_hours}h
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <BookOpen className="h-3 w-3" style={{ color: colors.textMuted }} />
                  <span className="text-xs" style={{ color: colors.textMuted }}>
                    {course.lessons?.length || 0} lessons
                  </span>
                </div>
                <Badge 
                  variant="outline" 
                  className="text-xs"
                  style={{ 
                    borderColor: difficultyColors[course.difficulty],
                    color: difficultyColors[course.difficulty]
                  }}
                >
                  {course.difficulty}
                </Badge>
              </div>
            </div>
            
            {/* Action */}
            <div className="flex-shrink-0">
              {!isLocked && !isCompleted && (
                <Button 
                  size="sm"
                  style={{ backgroundColor: colors.gold, color: '#000' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStart(course.id);
                  }}
                  data-testid={`start-course-${index}`}
                >
                  {isCurrentCourse ? 'Continue' : 'Start'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {isLocked && (
                <Lock className="h-5 w-5" style={{ color: colors.textMuted }} />
              )}
              {isCompleted && (
                <CheckCircle2 className="h-6 w-6" style={{ color: colors.success }} />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function LearningPathDetail() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchPath();
  }, [pathId]);

  const fetchPath = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/paths/${pathId}?token=${token}`);
      const data = await res.json();
      
      if (data.success) {
        setPath(data.path);
      } else {
        toast.error('Learning path not found');
        navigate('/academy/paths');
      }
    } catch (err) {
      toast.error('Failed to load learning path');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const res = await fetch(`${API_URL}/api/academy/paths/${pathId}/enroll?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Enrolled in learning path!');
        fetchPath(); // Refresh to show progress
      } else {
        toast.error(data.error || 'Failed to enroll');
      }
    } catch (err) {
      toast.error('Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const handleStartCourse = async (courseId) => {
    // First enroll in the course if not already
    try {
      await fetch(`${API_URL}/api/academy/courses/${courseId}/start?token=${token}`, {
        method: 'POST'
      });
    } catch (err) {
      // Ignore if already enrolled
    }
    navigate(`/academy/courses/${courseId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.gold }} />
      </div>
    );
  }

  if (!path) return null;

  const progress = path.user_progress;
  const isEnrolled = !!progress;
  const progressPercent = progress?.progress_percent || 0;
  const completedCourses = progress?.courses_completed || [];
  const currentCourseIndex = progress?.current_course_index || 0;
  const courses = path.course_details || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Hero */}
      <div 
        className="relative h-64 bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${path.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200'})` 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/60 to-transparent" />
        
        <div className="absolute inset-0 p-6 flex flex-col justify-end">
          <div className="max-w-5xl mx-auto w-full">
            <Button
              variant="ghost"
              className="mb-4"
              onClick={() => navigate('/academy/paths')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Learning Paths
            </Button>
            
            <div className="flex items-start gap-2 mb-2">
              <Badge 
                style={{ 
                  backgroundColor: `${difficultyColors[path.difficulty]}20`,
                  color: difficultyColors[path.difficulty]
                }}
              >
                {path.difficulty}
              </Badge>
              {isEnrolled && progress?.status === 'completed' && (
                <Badge style={{ backgroundColor: `${colors.success}20`, color: colors.success }}>
                  <Trophy className="h-3 w-3 mr-1" />
                  Completed
                </Badge>
              )}
            </div>
            
            <h1 
              className="text-4xl font-bold mb-2"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              {path.title}
            </h1>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-5xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <CardContent className="p-6">
                <h2 
                  className="font-bold text-lg mb-3"
                  style={{ color: colors.textHeading }}
                >
                  About This Learning Path
                </h2>
                <p style={{ color: colors.textBody }}>{path.description}</p>
                
                {/* Stats */}
                <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t" style={{ borderColor: colors.border }}>
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" style={{ color: colors.gold }} />
                    <div>
                      <p className="text-sm" style={{ color: colors.textMuted }}>Courses</p>
                      <p className="font-bold" style={{ color: colors.textHeading }}>{courses.length}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" style={{ color: colors.gold }} />
                    <div>
                      <p className="text-sm" style={{ color: colors.textMuted }}>Duration</p>
                      <p className="font-bold" style={{ color: colors.textHeading }}>{path.estimated_hours}h</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5" style={{ color: colors.gold }} />
                    <div>
                      <p className="text-sm" style={{ color: colors.textMuted }}>Certificates</p>
                      <p className="font-bold" style={{ color: colors.textHeading }}>{courses.length}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Course List */}
            <div>
              <h2 
                className="font-bold text-xl mb-6"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                Learning Journey
              </h2>
              
              <div className="space-y-0">
                {courses.map((course, index) => {
                  const isCompleted = completedCourses.includes(course.id);
                  const isCurrentCourse = index === currentCourseIndex && !isCompleted;
                  // Courses are locked if the previous course is not completed (except first)
                  const isLocked = !isEnrolled || (index > 0 && !completedCourses.includes(courses[index - 1]?.id));
                  
                  return (
                    <CourseStep
                      key={course.id}
                      course={course}
                      index={index}
                      isCompleted={isCompleted}
                      isCurrentCourse={isCurrentCourse}
                      isLocked={isLocked && !isCompleted}
                      onStart={handleStartCourse}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            <Card 
              className="sticky top-6"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            >
              <CardContent className="p-6">
                {isEnrolled ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold" style={{ color: colors.textHeading }}>
                        Your Progress
                      </h3>
                      <span className="text-2xl font-bold" style={{ color: colors.gold }}>
                        {progressPercent}%
                      </span>
                    </div>
                    
                    <Progress value={progressPercent} className="h-3 mb-4" />
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span style={{ color: colors.textMuted }}>Completed</span>
                        <span style={{ color: colors.textHeading }}>
                          {completedCourses.length} of {courses.length} courses
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span style={{ color: colors.textMuted }}>Status</span>
                        <Badge 
                          style={{ 
                            backgroundColor: progress?.status === 'completed' 
                              ? `${colors.success}20` 
                              : `${colors.gold}20`,
                            color: progress?.status === 'completed' 
                              ? colors.success 
                              : colors.gold
                          }}
                        >
                          {progress?.status === 'completed' ? 'Completed' : 'In Progress'}
                        </Badge>
                      </div>
                    </div>
                    
                    {currentCourseIndex < courses.length && progress?.status !== 'completed' && (
                      <Button 
                        className="w-full mt-6"
                        style={{ backgroundColor: colors.gold, color: '#000' }}
                        onClick={() => handleStartCourse(courses[currentCourseIndex].id)}
                        data-testid="continue-path-btn"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Continue Learning
                      </Button>
                    )}
                    
                    {progress?.status === 'completed' && (
                      <div 
                        className="mt-6 p-4 rounded-lg text-center"
                        style={{ backgroundColor: `${colors.success}10` }}
                      >
                        <Trophy className="h-8 w-8 mx-auto mb-2" style={{ color: colors.success }} />
                        <p className="font-medium" style={{ color: colors.success }}>
                          Congratulations!
                        </p>
                        <p className="text-sm" style={{ color: colors.textBody }}>
                          You've completed this learning path
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      <Route className="h-12 w-12 mx-auto mb-3" style={{ color: colors.gold }} />
                      <h3 className="font-bold text-lg" style={{ color: colors.textHeading }}>
                        Start Your Journey
                      </h3>
                      <p className="text-sm mt-1" style={{ color: colors.textBody }}>
                        Enroll to track your progress and unlock certificates
                      </p>
                    </div>
                    
                    <Button 
                      className="w-full"
                      style={{ backgroundColor: colors.gold, color: '#000' }}
                      onClick={handleEnroll}
                      disabled={enrolling}
                      data-testid="enroll-path-btn"
                    >
                      {enrolling ? (
                        'Enrolling...'
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Start Learning Path
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* What You'll Learn */}
            <Card style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4" style={{ color: colors.textHeading }}>
                  What You'll Learn
                </h3>
                <div className="space-y-3">
                  {[
                    { icon: Target, text: 'Master key concepts step by step' },
                    { icon: Zap, text: 'Apply skills in real scenarios' },
                    { icon: Award, text: 'Earn certificates for each course' },
                    { icon: Trophy, text: 'Complete the full learning path' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${colors.gold}20` }}
                      >
                        <item.icon className="h-4 w-4" style={{ color: colors.gold }} />
                      </div>
                      <span className="text-sm" style={{ color: colors.textBody }}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
